/**
 * useRequests Hook (Legacy)
 *
 * Provides CRUD operations for requests (care/ticketing module).
 * Updated to use v2 column names (summary, details, category, triage_owner_id)
 * while maintaining the same external interface for backward compatibility.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgId } from "./useOrganization";
import { useRealtimeTable } from "./useRealtimeTable";
import { useAuth } from "./useAuth";
import { toast } from "sonner";
import { toastWithAction } from "@/lib/toasts";
import type { Database } from "@/integrations/supabase/types";
import { logRequestAudit } from "@/lib/audit/requests";
import { isSupabaseUnavailableError } from "@/lib/supabase-compat";
import { toUiRequestCategory } from "@/lib/request-category-compat";
import { queryKeys } from "@/lib/query-keys";

import { parseLocalDate } from "@/lib/date-utils";
type RequestRow = Database['public']['Tables']['requests']['Row'];
type RequestUpdate = Database['public']['Tables']['requests']['Update'];

// V2 enum types from the updated schema
export type RequestType = Database['public']['Enums']['request_type'];
export type RequestPriority = Database['public']['Enums']['request_priority'];
export type RequestStatus = Database['public']['Enums']['request_status'];
export type VisibilityLevel = Database['public']['Enums']['visibility_level'];
export type ContactMethod = 'phone' | 'email' | 'sms' | 'in_person' | 'any';
export type CareRequestType = 'hospital_visit' | 'counseling' | 'follow_up' | 'prayer' | 'other' | 'visit' | 'meal' | 'benevolence';

export interface RequestWithDetails extends RequestRow {
  // Joined relations
  person?: {
    id: string;
    first_name: string;
    last_name: string;
    photo_url?: string | null;
    phone?: string | null;
    email?: string | null;
  } | null;
  household?: {
    id: string;
    name: string;
  } | null;
  owner?: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
  claimed_by?: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
  team?: {
    id: string;
    name: string;
  } | null;
  // Legacy field aliases for backward compat (map from v2 columns)
  title: string;
  description: string | null;
  type: string;
  owner_id: string | null;
  due_date: string | null;
  // Extended fields (available after pastoral care migration is applied)
  notes?: string | null;
  care_type?: CareRequestType;
  urgency?: RequestPriority;
  add_to_prayer_list?: boolean;
  location?: string | null;
  location_notes?: string | null;
  follow_up_date?: string | null;
  follow_up_owner_id?: string | null;
  team_id?: string | null;
  claimed_by_id?: string | null;
  claimed_at?: string | null;
  escalate_after_hours?: number | null;
  escalated_at?: string | null;
  escalated_to_id?: string | null;
}

const unavailableRequestsOrgs = new Set<string>();
const REQUEST_BULK_DELETE_CHUNK_SIZE = 100;

function isRequestsUnavailable(orgId: string | null | undefined) {
  return !!orgId && unavailableRequestsOrgs.has(orgId);
}

function markRequestsUnavailable(orgId: string | null | undefined) {
  if (orgId) unavailableRequestsOrgs.add(orgId);
}

type RequestQueryRow = RequestRow & {
  person?: RequestWithDetails['person'];
  household?: RequestWithDetails['household'];
  owner?: RequestWithDetails['owner'];
  claimed_by?: RequestWithDetails['claimed_by'];
  team?: RequestWithDetails['team'];
  summary?: string | null;
  details?: string | null;
  category?: string | null;
  triage_owner_id?: string | null;
  due_at?: string | null;
};

// Map v2 row to RequestWithDetails shape, providing backward-compatible aliases
const mapRowToDetails = (row: RequestQueryRow): RequestWithDetails => ({
  ...row,
  title: row.summary ?? '',
  description: row.details ?? null,
  type: toUiRequestCategory(row.category, row),
  owner_id: row.triage_owner_id ?? null,
  due_date: row.due_at ?? null,
  person: row.person || null,
  household: row.household || null,
  owner: row.owner || null,
  claimed_by: row.claimed_by || null,
  team: row.team || null,
});

const isUuid = (value: string | undefined): value is string =>
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value || "");

/**
 * Fetch all requests for the current organization
 */
export function useRequests(
  statusFilter?: RequestStatus | 'all',
  options?: {
    enabled?: boolean;
    personId?: string;
    householdId?: string;
    cacheScope?: string;
  }
) {
  const organizationId = useOrgId();
  const queryClient = useQueryClient();
  const isEnabled = options?.enabled ?? true;

  // Realtime: subscribe to requests INSERT/UPDATE/DELETE events.
  // When any staff member claims, updates, or resolves a request,
  // all other staff viewing the board see the change immediately.
  useRealtimeTable({
    table: "requests",
    events: ["INSERT", "UPDATE", "DELETE"],
    onEvent: () => {
      queryClient.invalidateQueries({ queryKey: ["requests"] });
    },
    enabled: !!organizationId && isEnabled && !isRequestsUnavailable(organizationId),
  });

  return useQuery({
    queryKey: [
      'requests',
      organizationId,
      statusFilter,
      options?.personId,
      options?.householdId,
      ...(options?.cacheScope === undefined ? [] : [{ cacheScope: options.cacheScope }]),
    ],
    queryFn: async () => {
      if (!organizationId) return [];
      if (isRequestsUnavailable(organizationId)) return [];

      let query = supabase
        .from('requests')
        .select('*, person:people!requests_requester_person_id_fkey(id, first_name, last_name, photo_url, phone, email)')
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(2000); // Safety net: prevents silent truncation at Supabase max_rows=1000

      if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (options?.personId) {
        query = query.eq('requester_person_id', options.personId);
      }

      const { data, error } = await query;

      if (error) {
        if (isSupabaseUnavailableError(error)) {
          markRequestsUnavailable(organizationId);
          return [];
        }
        throw error;
      }

      return (data || []).map((row) => mapRowToDetails(row as RequestQueryRow));
    },
    enabled: !!organizationId && isEnabled && !isRequestsUnavailable(organizationId),
  });
}

/**
 * Fetch a single request by ID
 */
export function useRequest(requestId: string | undefined) {
  const organizationId = useOrgId();
  const hasValidRequestId = isUuid(requestId);

  return useQuery({
    queryKey: ['request', hasValidRequestId ? requestId : null],
    queryFn: async () => {
      if (!hasValidRequestId || !organizationId) return null;
      if (isRequestsUnavailable(organizationId)) return null;

      const { data, error } = await supabase
        .from('requests')
        .select('*, person:people!requests_requester_person_id_fkey(id, first_name, last_name, photo_url, phone, email)')
        .eq('id', requestId)
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        if (isSupabaseUnavailableError(error)) {
          markRequestsUnavailable(organizationId);
          return null;
        }
        throw error;
      }

      return mapRowToDetails(data);
    },
    enabled: hasValidRequestId && !!organizationId && !isRequestsUnavailable(organizationId),
  });
}

/**
 * Update an existing request.
 *
 * Auto-fills the resolved_at / closed_at timestamp columns when status
 * transitions into "resolved" / "closed" — those columns existed on the
 * v2 schema but were never being set client-side, so reports that grouped
 * by resolution week saw all rows fall into the "unresolved" bucket.
 */
export function useUpdateRequest() {
  const queryClient = useQueryClient();
  const organizationId = useOrgId();

  return useMutation({
    mutationFn: async ({
      id,
      updates
    }: {
      id: string;
      updates: Partial<RequestUpdate & Record<string, unknown>>;
    }) => {
      if (!organizationId) throw new Error("No organization selected");

      // Mirror status transitions to the resolution / closure timestamps so
      // downstream analytics ("resolved this week", "open > 7d") match the
      // status column. Caller-provided values win — explicit beats implicit.
      const decorated: typeof updates = { ...updates };
      if (decorated.status === 'resolved' && decorated.resolved_at === undefined) {
        decorated.resolved_at = new Date().toISOString();
      }
      if (decorated.status === 'closed' && decorated.closed_at === undefined) {
        decorated.closed_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('requests')
        .update(decorated)
        // Defense-in-depth: scope every mutation to the active org so a
        // mis-typed id or a stale RLS policy cannot cross tenants.
        .eq('id', id)
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .select()
        .single();

      if (error) {
        console.error('Error updating request:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['request', data.id] });
      toast.success('Request updated');
    },
    onError: (error) => {
      toast.error('Failed to update request', {
        description: error.message,
      });
    },
  });
}

/**
 * Update request status with undo capability
 */
export function useUpdateRequestStatus() {
  const updateRequest = useUpdateRequest();
  const { user } = useAuth();
  const organizationId = useOrgId();
  const queryClient = useQueryClient();

  const updateStatus = async (
    id: string,
    newStatus: RequestStatus,
    previousStatus: RequestStatus
  ) => {
    const updated = await updateRequest.mutateAsync({
      id,
      updates: { status: newStatus },
    });

    if (updated && organizationId) {
      await logRequestAudit({
        action: "STATUS_UPDATE",
        organizationId,
        recordId: updated.id,
        userId: user?.id ?? null,
        oldData: { status: previousStatus },
        newData: { status: newStatus },
      });
      await queryClient.invalidateQueries({
        queryKey: ["request-audit-logs", organizationId, updated.id],
      });
    }

    const personId = updated?.requester_person_id ?? updated?.person_id;
    if (personId) {
      // supabase-js does not reject on database errors — it resolves with
      // { error }. The previous try/catch was dead code: the toast warning
      // never fired. Check the destructured error and surface it explicitly.
      const { error: notifyError } = await supabase
        .from("notifications")
        .insert({
          organization_id: updated.organization_id,
          person_id: personId,
          title: "Request status updated",
          body: `Your request "${updated.summary}" is now ${newStatus.replace("_", " ")}.`,
          category: "requests",
          link_url: "/member/requests",
          link_label: "View requests",
        });

      if (notifyError) {
        console.error("Failed to notify requester:", notifyError);
        toast.message("Status updated", {
          description: "We couldn't send a notification to the requester.",
        });
      }
    }

    toastWithAction(`Status updated to ${newStatus.replace('_', ' ')}`, {
      // Stable per-request id: rapid status flips replace the toast
      // instead of stacking duplicates.
      id: `request-status-${id}`,
      action: {
        label: 'Undo',
        onClick: () => {
          updateRequest.mutate({
            id,
            updates: { status: previousStatus },
          });
        },
      },
    });
  };

  return {
    updateStatus,
    isPending: updateRequest.isPending,
  };
}

/**
 * Soft-delete a request.
 *
 * Hard-deleting a request would also cascade `tasks.request_id` to NULL
 * (per the `tasks_request_org_fkey` ON DELETE SET NULL constraint), which
 * silently orphans linked tasks from their parent context. Soft-delete
 * keeps the link intact for audit and restore.
 */
export function useDeleteRequest() {
  const queryClient = useQueryClient();
  const organizationId = useOrgId();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!organizationId) throw new Error("No organization selected");

      const { error } = await supabase.rpc('set_requests_deleted_at', {
        p_organization_id: organizationId,
        p_ids: [id],
        p_deleted_at: new Date().toISOString(),
      });

      if (error) {
        console.error('Error deleting request:', error);
        throw error;
      }

      // Audit the deletion so the request still has a paper trail even
      // though it disappears from staff views.
      await logRequestAudit({
        action: "DELETE",
        organizationId,
        recordId: id,
        userId: user?.id ?? null,
        oldData: { deleted_at: null },
        newData: { deleted_at: new Date().toISOString() },
      });

      return id;
    },
    onSuccess: (id) => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['request', id] });
      toast.success('Request deleted');
    },
    onError: (error) => {
      toast.error('Failed to delete request', {
        description: error.message,
      });
    },
  });
}

export function useDeleteRequests() {
  const queryClient = useQueryClient();
  const organizationId = useOrgId();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (ids: string[]) => {
      if (!organizationId) throw new Error("No organization selected");
      const uniqueIds = Array.from(new Set(ids)).filter(Boolean);
      if (uniqueIds.length === 0) return { deletedCount: 0, ids: [] as string[] };

      const deletedAt = new Date().toISOString();
      const deletedIds: string[] = [];

      for (let index = 0; index < uniqueIds.length; index += REQUEST_BULK_DELETE_CHUNK_SIZE) {
        const chunk = uniqueIds.slice(index, index + REQUEST_BULK_DELETE_CHUNK_SIZE);
        const { data: existingRequests, error: existingError } = await supabase
          .from("requests")
          .select("id")
          .eq("organization_id", organizationId)
          .in("id", chunk)
          .is("deleted_at", null);

        if (existingError) {
          console.error("Error loading requests to delete:", existingError);
          throw existingError;
        }

        const chunkDeletedIds = (existingRequests ?? []).map((request) => request.id);
        if (chunkDeletedIds.length === 0) continue;

        const { error } = await supabase.rpc("set_requests_deleted_at", {
          p_organization_id: organizationId,
          p_ids: chunkDeletedIds,
          p_deleted_at: deletedAt,
        });

        if (error) {
          console.error("Error deleting requests:", error);
          throw error;
        }

        deletedIds.push(...chunkDeletedIds);
        await Promise.all(
          chunkDeletedIds.map((id) =>
            logRequestAudit({
              action: "DELETE",
              organizationId,
              recordId: id,
              userId: user?.id ?? null,
              oldData: { deleted_at: null },
              newData: { deleted_at: deletedAt },
            })
          )
        );
      }

      return { deletedCount: deletedIds.length, ids: deletedIds };
    },
    onSuccess: ({ deletedCount, ids }) => {
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      ids.forEach((id) => {
        queryClient.invalidateQueries({ queryKey: ["request", id] });
      });
      toast.success("Requests deleted", {
        description: `${deletedCount} request${deletedCount === 1 ? "" : "s"} removed.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to delete requests", {
        description: error.message,
      });
    },
  });
}

/**
 * Claim a request (volunteer to handle it).
 *
 * Two staff hitting the same row simultaneously was a last-write-wins race
 * — both would see "Claimed by you" optimistically, the loser would notice
 * later that the requester had been double-contacted. We now scope the
 * UPDATE to claimed_by_id IS NULL so the database picks the winner, then
 * verify a row was actually returned and surface a clear error to the loser.
 */
export function useClaimRequest() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = useOrgId();

  return useMutation({
    mutationFn: async (requestId: string) => {
      if (!user) {
        throw new Error('Not authenticated');
      }
      if (!organizationId) throw new Error("No organization selected");

      const { data, error } = await supabase
        .from('requests')
        .update({
          claimed_by_id: user.id,
          claimed_at: new Date().toISOString(),
          status: 'in_progress',
        })
        .eq('id', requestId)
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        // Race guard — only claim rows that nobody has already claimed.
        .is('claimed_by_id', null)
        .select()
        .maybeSingle();

      if (error) {
        console.error('Error claiming request:', error);
        throw error;
      }

      if (!data) {
        // Either another user beat us to the claim, or the request was
        // soft-deleted between fetch and update. Refresh so UI catches up.
        throw new Error('This request was already claimed by someone else.');
      }

      await logRequestAudit({
        action: "CLAIM",
        organizationId,
        recordId: data.id,
        userId: user.id,
        oldData: { claimed_by_id: null },
        newData: { claimed_by_id: user.id, claimed_at: data.claimed_at },
      });

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['request', data.id] });
      queryClient.invalidateQueries({ queryKey: queryKeys.workItems.lists() });
      toast.success('Request claimed', {
        description: 'You are now responsible for this request.',
      });
    },
    onError: (error) => {
      // A claim can fail because another user won the race; refresh unified
      // work-item/SLA projections so queue surfaces stop showing stale rows.
      queryClient.invalidateQueries({ queryKey: queryKeys.workItems.lists() });
      toast.error('Failed to claim request', {
        description: error.message,
      });
    },
  });
}

/**
 * Release a claimed request (unclaim)
 */
export function useReleaseRequest() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = useOrgId();

  return useMutation({
    mutationFn: async (requestId: string) => {
      if (!user) {
        throw new Error('Not authenticated');
      }
      if (!organizationId) throw new Error("No organization selected");

      const { data, error } = await supabase
        .from('requests')
        .update({
          claimed_by_id: null,
          claimed_at: null,
          status: 'open',
        })
        .eq('id', requestId)
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        // Only the current claimant may release — prevents a stale tab from
        // releasing a request that has since been re-assigned to someone else.
        .eq('claimed_by_id', user.id)
        .select()
        .maybeSingle();

      if (error) {
        console.error('Error releasing request:', error);
        throw error;
      }

      if (!data) {
        throw new Error('This request is not yours to release.');
      }

      await logRequestAudit({
        action: "RELEASE",
        organizationId,
        recordId: data.id,
        userId: user.id,
        oldData: { claimed_by_id: user.id },
        newData: { claimed_by_id: null, claimed_at: null },
      });

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['request', data.id] });
      toast.success('Request released', {
        description: 'The request is now available for others to claim.',
      });
    },
    onError: (error) => {
      toast.error('Failed to release request', {
        description: error.message,
      });
    },
  });
}

/**
 * Schedule a follow-up for a request
 */
export function useScheduleFollowUp() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const organizationId = useOrgId();

  return useMutation({
    mutationFn: async ({
      requestId,
      followUpDate,
      followUpOwnerId,
    }: {
      requestId: string;
      followUpDate: string;
      followUpOwnerId?: string;
    }) => {
      if (!user) {
        throw new Error('Not authenticated');
      }
      if (!organizationId) throw new Error("No organization selected");

      const { data, error } = await supabase
        .from('requests')
        .update({
          follow_up_date: followUpDate,
          follow_up_owner_id: followUpOwnerId || user.id,
        })
        .eq('id', requestId)
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .select()
        .single();

      if (error) {
        console.error('Error scheduling follow-up:', error);
        throw error;
      }

      await logRequestAudit({
        action: "SCHEDULE_FOLLOW_UP",
        organizationId,
        recordId: data.id,
        userId: user.id,
        oldData: null,
        newData: { follow_up_date: followUpDate, follow_up_owner_id: followUpOwnerId || user.id },
      });

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      queryClient.invalidateQueries({ queryKey: ['request', data.id] });
      toast.success('Follow-up scheduled', {
        description: `Follow-up scheduled for ${(parseLocalDate(data.follow_up_date) ?? new Date(data.follow_up_date)).toLocaleDateString()}.`,
      });
    },
    onError: (error) => {
      toast.error('Failed to schedule follow-up', {
        description: error.message,
      });
    },
  });
}
